import { useEffect, useId, useState, type FormEvent } from "react";
import { DIALER_SKIP_REASONS, type DialerSkipReason } from "@gioia365/contracts";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { RadioGroup } from "../design/components/RadioGroup";
import { messageForError } from "../i18n/errors";
import { useLocale, useT } from "../i18n/locale";
import { useDialerSession } from "./dialer-session";

// «Salta questo Lead» (piano M5, Task 5, D5; mock-up `scheda-lead.html`, `m-skip`): soltanto prima di aver chiamato il Lead,
// con uno dei quattro motivi, che resta nella Timeline. Mentre la finestra è aperta la chiamata non parte; «Annulla» la lascia
// ripartire. Un salto rifiutato lascia la finestra aperta con il motivo del rifiuto: il Lead non si chiama da solo.

export function SkipDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const { session, hold, skip, busy, notice } = useDialerSession();
  const formId = useId();
  const [reason, setReason] = useState<DialerSkipReason>(DIALER_SKIP_REASONS[0]);
  const [refused, setRefused] = useState(false);

  // Dalla cascata dai Task il Lead saltato tiene il suo richiamo nei Task, non un posto in una lista (questione 339).
  const fromTasks = session?.source.type === "task_view";

  useEffect(() => hold(), [hold]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (await skip({ reason, note: null })) onClose();
    else setRefused(true);
  }

  return (
    <Dialog
      open
      title={t("dialer.skipTitle")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={busy} aria-busy={busy ? "true" : undefined}>
            {t("dialer.skipGo")}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <RadioGroup
          name={`${formId}-reason`}
          legend={t("dialer.skipLegend")}
          options={DIALER_SKIP_REASONS.map((value) => {
            const text = t(`dialer.skipReasons.${value}`);
            return { value, label: text.charAt(0).toLocaleUpperCase(locale) + text.slice(1) };
          })}
          value={reason}
          onChange={setReason}
        />
        <p className="g-field__hint">{t(fromTasks ? "dialer.skipHintTasks" : "dialer.skipHint")}</p>
        {refused && notice ? (
          <p className="g-field__error" role="alert">
            {messageForError(t, notice)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
